import React, { useEffect, useMemo, useState } from "react";
import { ImageIcon, Pencil, Trash2, X } from "lucide-react";
import { detectMediaType, formatFieldTitle, resolveMediaUrl, sanitizePathForDisplay } from "./contentManagerUtils";

const EditableContentRow = ({
  item,
  t,
  onSaveValue,
  onSaveOrder,
  onToggleActive,
  onUploadMedia,
  onRemoveMedia,
  onDeleteItem,
}) => {
  const [draftValue, setDraftValue] = useState(
    typeof item.value === "string" || typeof item.value === "number" ? String(item.value ?? "") : ""
  );
  const [draftOrder, setDraftOrder] = useState(Number(item.order) || 0);
  const [uploadFile, setUploadFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    setDraftValue(typeof item.value === "string" || typeof item.value === "number" ? String(item.value ?? "") : "");
    setDraftOrder(Number(item.order) || 0);
    setUploadFile(null);
    setImageFailed(false);
    setVideoFailed(false);
  }, [item.value, item.id, item.path]);

  const mediaSource = useMemo(() => {
    const fromMedia = resolveMediaUrl(item.mediaUrl);
    if (fromMedia) return fromMedia;
    return resolveMediaUrl(draftValue);
  }, [item.mediaUrl, draftValue]);
  const mediaType = detectMediaType(mediaSource);
  const hasUploadsValue = useMemo(() => String(draftValue || "").trim().startsWith("/uploads/"), [draftValue]);

  const displayPath = sanitizePathForDisplay(item.path);
  const fieldLabel = /^\d+$/.test(String(item.label || ""))
    ? formatFieldTitle(item.key || "Field")
    : formatFieldTitle(item.label);

  const previewText = draftValue?.trim() || "No content yet";
  const isLongValue = previewText.length > 120;

  return (
    <>
      <article className="group rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-semibold text-slate-900">{fieldLabel}</h4>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-600">
                {item.type || "text"}
              </span>
              {!item.isActive ? (
                <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                  Inactive
                </span>
              ) : null}
            </div>
            {displayPath ? (
              <p className="mt-1 text-xs text-slate-400 font-mono truncate max-w-xl">{displayPath}</p>
            ) : null}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#0b1f3f] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#132d54]"
            >
              <Pencil size={14} />
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDeleteItem(item)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50"
            >
              <Trash2 size={14} />
              <span className="sr-only sm:not-sr-only">Delete</span>
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <div className="min-w-0 flex-1 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
            <p className={`text-sm text-slate-700 break-words ${isLongValue ? "line-clamp-3" : ""}`}>
              {previewText}
            </p>
          </div>

          {hasUploadsValue && mediaSource ? (
            <div className="shrink-0 w-full sm:w-28 h-20 rounded-lg border border-slate-200 overflow-hidden bg-slate-100">
              {mediaType === "image" && !imageFailed ? (
                <img
                  src={mediaSource}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={() => setImageFailed(true)}
                />
              ) : mediaType === "video" && !videoFailed ? (
                <video
                  src={mediaSource}
                  className="w-full h-full object-cover"
                  muted
                  onError={() => setVideoFailed(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <ImageIcon size={20} />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </article>

      {isModalOpen ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col">
            <div className="shrink-0 border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-lg font-semibold text-slate-900">{fieldLabel}</p>
                {displayPath ? <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">{displayPath}</p> : null}
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="shrink-0 rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Content value</label>
                <textarea
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  className="w-full min-h-[160px] rounded-xl border border-slate-300 bg-white p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#0b1f3f]/20 focus:border-[#0b1f3f]"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSaveValue(item, draftValue)}
                    className="rounded-lg bg-[#FE7E3F] px-4 py-2 text-sm font-medium text-white hover:bg-[#e86f32]"
                  >
                    {t("content.saveValue")}
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-800 mb-3">Item settings</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="text-sm text-slate-700">
                    Display order
                    <input
                      type="number"
                      value={draftOrder}
                      onChange={(e) => setDraftOrder(Number(e.target.value) || 0)}
                      className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b1f3f]/20"
                    />
                  </label>
                  <label className="text-sm text-slate-700 flex items-end gap-2 pb-2">
                    <input
                      type="checkbox"
                      checked={Boolean(item.isActive)}
                      onChange={(e) => onToggleActive(item, e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Visible on website
                  </label>
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSaveOrder(item, draftOrder)}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {t("content.saveOrder")}
                  </button>
                </div>
              </div>

              {hasUploadsValue ? (
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-800 mb-3">Media</p>
                  {mediaSource ? (
                    <div className="mb-3 rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                      {mediaType === "image" && !imageFailed ? (
                        <img src={mediaSource} alt="" className="w-full max-h-48 object-contain" onError={() => setImageFailed(true)} />
                      ) : mediaType === "video" && !videoFailed ? (
                        <video src={mediaSource} controls className="w-full max-h-48" onError={() => setVideoFailed(true)} />
                      ) : (
                        <p className="p-3 text-xs text-slate-500 break-all">{mediaSource}</p>
                      )}
                    </div>
                  ) : null}
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-200 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-300"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => uploadFile && onUploadMedia(item, uploadFile)}
                      disabled={!uploadFile}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {t("content.uploadMedia")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemoveMedia(item)}
                      className="rounded-lg border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
                    >
                      {t("content.removeMedia")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default EditableContentRow;
